/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Button, Grid, IconButton, Link, Typography2, breakpoints } from '@coursera/cds-core';

type CtaButtonProps = {
  label: string;
  onClick?: () => void;
  href?: string;
  isLoading?: boolean;
};

type TextLayout = 'single-line' | 'separate-lines';

type Props = {
  headerText: string;
  bodyText: string;
  isDismissed: boolean;
  onDismiss: () => void;
  ctaButton: CtaButtonProps;
  textLayout?: TextLayout;
};

const styles = {
  bannerContainer: css`
    border-radius: var(--cds-spacing-200);
    background: var(--cds-color-emphasis-primary-background-xweak);

    &.cds-grid-item {
      padding: var(--cds-spacing-150) var(--cds-spacing-300);
    }

    ${breakpoints.down('sm')} {
      &.cds-grid-item {
        padding: var(--cds-spacing-200);
      }
    }

    ${breakpoints.up('md')} {
      align-items: center;
    }
  `,
  cta: css`
    ${breakpoints.down('sm')} {
      padding: 0;
    }

    margin-right: var(--cds-spacing-150);
    font-weight: 600;
  `,
  closeButton: css`
    svg {
      color: var(--cds-color-neutral-primary);

      ${breakpoints.down('sm')} {
        align-self: flex-start;
      }
    }
  `,
  textContainer: (isSingleLine: boolean) => css`
    width: fit-content;
    gap: ${isSingleLine ? 'var(--cds-spacing-50)' : '0'};

    ${breakpoints.down('sm')} {
      gap: var(--cds-spacing-100);
    }
  `,
};

const InformationBanner = ({
  isDismissed,
  onDismiss,
  ctaButton,
  headerText,
  bodyText,
  textLayout = 'single-line',
}: Props) => {
  if (isDismissed) {
    return null;
  }

  const isSingleLine = textLayout === 'single-line';

  const { label, onClick, href, isLoading } = ctaButton;

  const renderCta = () => {
    if (href) {
      return (
        <Link href={href} css={styles.cta} variant="quiet" typographyVariant="bodySecondary">
          {label}
        </Link>
      );
    }

    return (
      <Button css={styles.cta} variant="ghost" size="small" onClick={onClick} loading={isLoading}>
        {label}
      </Button>
    );
  };

  return (
    <Grid container item xs={12} css={styles.bannerContainer}>
      <Grid
        item
        xs
        container
        spacing={8}
        alignItems="center"
        justifyContent={isSingleLine ? 'flex-start' : 'space-between'}
      >
        <Grid
          item
          container
          alignItems={isSingleLine ? 'center' : 'flex-start'}
          component="div"
          direction={isSingleLine ? 'row' : 'column'}
          css={styles.textContainer(isSingleLine)}
        >
          <Grid item sm={12} md="auto">
            <Typography2 variant="subtitleMedium" component="h2">
              {headerText}
            </Typography2>
          </Grid>
          <Grid item sm={12} md="auto">
            <Typography2 variant="bodyPrimary" component="p">
              {bodyText}
            </Typography2>
          </Grid>
        </Grid>
        <Grid item sm={12} md="auto">
          {renderCta()}
        </Grid>
      </Grid>
      <Grid item xs="auto" css={styles.closeButton}>
        <IconButton size="small" intent="close" variant="ghost" onClick={onDismiss} />
      </Grid>
    </Grid>
  );
};

export default InformationBanner;
